import { getSessionId } from "@/analytics";
import { api } from "@/api";
import { getChatApi } from "@/chatApi";
import { storage } from "@/utils";
import { SendMessageRequest, SendMessageResponse } from "@/types";

/**
 * Send a message to the chat widget and get the response with full conversation history
 *
 * @param siteId - TOMIS site slug
 * @param message - User message text
 * @param action - Optional action payload (e.g. form submit)
 * @param faqId - Optional FAQ ID to short-circuit backend and directly link to an FAQ
 * @param welcomeButtonId - Clicked welcome button, which holds the action shown after the FAQ
 * @returns Promise resolving to conversation response or error
 */
export async function sendMessage(
  siteId: string,
  message: string,
  action?: SendMessageRequest["action"],
  faqId?: number,
  welcomeButtonId?: number,
): Promise<{
  data?: SendMessageResponse;
  error?: string;
}> {
  const userId = storage.getUserId(siteId);
  const conversationId = storage.getConversationId(siteId);

  if (!userId) {
    return {
      error: "User ID not found. Please try refreshing the page.",
    };
  }

  const requestBody: SendMessageRequest = {
    user_id: userId,
    message,
    page_url: window.location.href,
    test_mode: getChatApi().chat.test_mode || false,
  };

  const sessionId = getSessionId();
  if (sessionId) {
    requestBody.session_id = sessionId;
  }

  // Add conversation_id if we have an existing conversation
  if (conversationId) {
    requestBody.conversation_id = conversationId;
  }

  // If action is provided, use it (takes precedence)
  if (action) {
    requestBody.action = action;
  } else if (faqId !== undefined) {
    // If faqId is provided, create a quick_reply action
    requestBody.action = {
      category: "quick_reply",
      faq_id: faqId,
      ...(welcomeButtonId !== undefined
        ? { welcome_button_id: welcomeButtonId }
        : {}),
    };
  }

  const { data, error } = await api.post<SendMessageResponse>(
    `/${siteId}/chat-widget/message/`,
    requestBody,
  );

  if (error || !data) {
    return {
      error: error || "Failed to send message",
    };
  }

  // Update conversation ID in storage
  storage.setConversationId(data.conversation_id, siteId);

  return { data };
}

/**
 * Restart the conversation by clearing the stored conversation ID
 * This will cause the next message to create a new conversation
 *
 * @param siteId - TOMIS site slug
 */
export function restartConversation(siteId: string): void {
  storage.setConversationId("", siteId);
}
